import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Actions from '../../flux/Actions';
import ProjectStore from '../../flux/stores/ProjectStore';
import {
   createEntityMentionExtension,
   type EntityMentionItem,
   type MentionedEntityType,
} from './editor/entityMentions';
import { SlashCommands } from './editor/slashCommands';

const LEGACY_PLACEHOLDER = '<p class="text-ash">Begin writing your scene...</p>';

const normalizeSceneContent = (content = '') =>
   content.trim() === LEGACY_PLACEHOLDER ? '' : content;

const getEditorHTML = (editor: Editor) => (editor.isEmpty ? '' : editor.getHTML());

const buildEntityMentionItems = (): EntityMentionItem[] => [
   ...ProjectStore.getCharacters()
      .filter((character) => character.id && character.name)
      .map((character) => ({
         id: character.id!,
         label: character.name!,
         title: `@${character.name}`,
         subtitle: 'Character',
         entityType: 'character' as const,
      })),
   ...ProjectStore.getLocations()
      .filter((location) => location.id && location.name)
      .map((location) => ({
         id: location.id!,
         label: location.name!,
         title: `@${location.name}`,
         subtitle: 'Location',
         entityType: 'location' as const,
      })),
   ...ProjectStore.getPlots()
      .filter((plot) => plot.id && plot.title)
      .map((plot) => ({
         id: plot.id!,
         label: plot.title!,
         title: `@${plot.title}`,
         subtitle: 'Plot',
         entityType: 'plot' as const,
      })),
];

interface ToolbarButtonProps {
   active?: boolean;
   disabled?: boolean;
   label: string;
   title: string;
   onClick: () => void;
}

function ToolbarButton({
   active = false,
   disabled = false,
   label,
   title,
   onClick,
}: ToolbarButtonProps) {
   return (
      <button
         type="button"
         className={`tiptap-toolbar-button${active ? ' is-active' : ''}`}
         disabled={disabled}
         title={title}
         aria-label={title}
         aria-pressed={active}
         onMouseDown={(event) => event.preventDefault()}
         onClick={onClick}
      >
         {label}
      </button>
   );
}

function EditorToolbar({ editor }: { editor: Editor }) {
   const state = useEditorState({
      editor,
      selector: ({ editor: currentEditor }) => ({
         bold: currentEditor.isActive('bold'),
         italic: currentEditor.isActive('italic'),
         underline: currentEditor.isActive('underline'),
         strike: currentEditor.isActive('strike'),
         highlight: currentEditor.isActive('highlight'),
         blockquote: currentEditor.isActive('blockquote'),
         bulletList: currentEditor.isActive('bulletList'),
         orderedList: currentEditor.isActive('orderedList'),
         codeBlock: currentEditor.isActive('codeBlock'),
         textAlign:
            currentEditor.getAttributes('paragraph').textAlign ||
            currentEditor.getAttributes('heading').textAlign ||
            'left',
         heading: ([1, 2, 3] as const).find((level) =>
            currentEditor.isActive('heading', { level }),
         ),
         fontSize: currentEditor.getAttributes('textStyle').fontSize || '',
         color: currentEditor.getAttributes('textStyle').color || '#ffffff',
         canUndo: currentEditor.can().chain().focus().undo().run(),
         canRedo: currentEditor.can().chain().focus().redo().run(),
      }),
   });

   return (
      <div className="tiptap-toolbar" role="toolbar" aria-label="Text formatting">
         <select
            className="tiptap-toolbar-select"
            aria-label="Text style"
            title="Text style"
            value={state?.heading ? `h${state.heading}` : 'paragraph'}
            onChange={(event) => {
               const value = event.target.value;
               if (value === 'paragraph') {
                  editor.chain().focus().setParagraph().run();
                  return;
               }
               editor
                  .chain()
                  .focus()
                  .setHeading({
                     level: Number(value.slice(1)) as 1 | 2 | 3,
                  })
                  .run();
            }}
         >
            <option value="paragraph">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
         </select>

         <select
            className="tiptap-toolbar-select tiptap-font-size"
            aria-label="Font size"
            title="Font size"
            value={state?.fontSize || ''}
            onChange={(event) => {
               if (!event.target.value) {
                  editor.chain().focus().unsetFontSize().run();
                  return;
               }
               editor.chain().focus().setFontSize(event.target.value).run();
            }}
         >
            <option value="">Size</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
            <option value="24px">24</option>
            <option value="32px">32</option>
            <option value="40px">40</option>
         </select>

         <label className="tiptap-color-control" title="Text color" aria-label="Text color">
            <span>A</span>
            <input
               type="color"
               value={/^#[0-9a-f]{6}$/i.test(state?.color || '') ? state?.color : '#ffffff'}
               onChange={(event) => editor.chain().focus().setColor(event.target.value).run()}
            />
         </label>

         <span className="tiptap-toolbar-divider" />

         <ToolbarButton
            label="B"
            title="Bold"
            active={state?.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}
         />
         <ToolbarButton
            label="I"
            title="Italic"
            active={state?.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
         />
         <ToolbarButton
            label="U"
            title="Underline"
            active={state?.underline}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
         />
         <ToolbarButton
            label="S"
            title="Strikethrough"
            active={state?.strike}
            onClick={() => editor.chain().focus().toggleStrike().run()}
         />
         <ToolbarButton
            label="HL"
            title="Highlight"
            active={state?.highlight}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
         />

         <span className="tiptap-toolbar-divider" />

         <ToolbarButton
            label="• List"
            title="Bullet list"
            active={state?.bulletList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
         />
         <ToolbarButton
            label="1. List"
            title="Numbered list"
            active={state?.orderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
         />
         <ToolbarButton
            label="Quote"
            title="Blockquote"
            active={state?.blockquote}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
         />
         <ToolbarButton
            label="Code"
            title="Code block"
            active={state?.codeBlock}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
         />

         <span className="tiptap-toolbar-divider" />

         {(['left', 'center', 'right'] as const).map((alignment) => (
            <ToolbarButton
               key={alignment}
               label={alignment === 'left' ? 'L' : alignment === 'center' ? 'C' : 'R'}
               title={`Align ${alignment}`}
               active={state?.textAlign === alignment}
               onClick={() => editor.chain().focus().setTextAlign(alignment).run()}
            />
         ))}

         <span className="tiptap-toolbar-spacer" />

         <ToolbarButton
            label="↶"
            title="Undo"
            disabled={!state?.canUndo}
            onClick={() => editor.chain().focus().undo().run()}
         />
         <ToolbarButton
            label="↷"
            title="Redo"
            disabled={!state?.canRedo}
            onClick={() => editor.chain().focus().redo().run()}
         />
      </div>
   );
}

interface EditorSurfaceProps {
   editor: Editor;
   isSaving: boolean;
   zen?: boolean;
}

function EditorSurface({ editor, isSaving, zen = false }: EditorSurfaceProps) {
   return (
      <div className={`tiptap-root${zen ? ' is-zen' : ''}`}>
         <EditorToolbar editor={editor} />
         <div className="relative">
            <EditorContent editor={editor} />
            {isSaving && (
               <div className="scene-editor-saving" aria-label="Saving">
                  <span className="scene-editor-saving-spinner" />
                  {zen && <span>Saving…</span>}
               </div>
            )}
         </div>
      </div>
   );
}

interface SceneEditorProps {
   sceneId: string;
   content: string;
}

function SceneEditor({ sceneId, content }: SceneEditorProps) {
   const normalizedContent = normalizeSceneContent(content);
   const [value, setValue] = useState(normalizedContent);
   const valueRef = useRef(normalizedContent);
   const [entityItems, setEntityItems] = useState(buildEntityMentionItems);
   const entityItemsRef = useRef(entityItems);
   const lastSavedRef = useRef(normalizedContent);
   const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const savingIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const cleanedPlaceholderRef = useRef(false);
   const [isSaving, setIsSaving] = useState(false);
   const [showZen, setShowZen] = useState(false);

   entityItemsRef.current = entityItems;
   valueRef.current = value;

   useEffect(() => {
      const handleStoreChange = () => {
         setEntityItems(buildEntityMentionItems());
      };

      handleStoreChange();
      ProjectStore.addChangeListener(handleStoreChange);
      return () => ProjectStore.removeChangeListener(handleStoreChange);
   }, []);

   const openEntity = useCallback((entityType: MentionedEntityType, entityId: string) => {
      setShowZen(false);

      switch (entityType) {
         case 'character':
            Actions.setActiveTab('characters');
            Actions.selectCharacter(entityId);
            break;
         case 'location':
            Actions.setActiveTab('locations');
            Actions.selectLocation(entityId);
            break;
         case 'plot':
            Actions.setActiveTab('plots');
            Actions.selectPlot(entityId);
            break;
         default:
      }
   }, []);

   const mentionExtension = useMemo(
      () => createEntityMentionExtension(() => entityItemsRef.current),
      [],
   );

   const editor = useEditor(
      {
         extensions: [
            StarterKit.configure({
               heading: { levels: [1, 2, 3] },
               link: false,
            }),
            TextStyleKit.configure({
               backgroundColor: false,
               fontFamily: false,
               lineHeight: false,
            }),
            Highlight,
            TextAlign.configure({
               types: ['heading', 'paragraph'],
            }),
            Placeholder.configure({
               placeholder: 'Begin writing your scene… Type @ to tag an entity or / for commands.',
            }),
            mentionExtension,
            SlashCommands,
         ],
         content: normalizedContent,
         editorProps: {
            attributes: {
               class: 'scene-editor-content',
               dir: 'ltr',
               spellcheck: 'true',
               'aria-label': 'Scene editor',
            },
            handleClickOn: (_view, _pos, node, _nodePos, event) => {
               if (node.type.name !== 'mention') return false;

               event.preventDefault();
               const entityType = node.attrs.entityType as MentionedEntityType | null;
               const entityId = node.attrs.id as string | null;
               if (entityType && entityId) {
                  openEntity(entityType, entityId);
               }
               return true;
            },
         },
         onUpdate: ({ editor: updatedEditor }) => {
            setValue(getEditorHTML(updatedEditor));
         },
      },
      [sceneId],
   );

   useEffect(() => {
      if (!editor) return;

      const incoming = normalizeSceneContent(content);
      const current = getEditorHTML(editor);

      if (
         incoming !== current &&
         incoming !== lastSavedRef.current &&
         valueRef.current === lastSavedRef.current
      ) {
         editor.commands.setContent(incoming, { emitUpdate: false });
         lastSavedRef.current = incoming;
         setValue(incoming);
      }

      if (incoming !== content && !cleanedPlaceholderRef.current) {
         cleanedPlaceholderRef.current = true;
         lastSavedRef.current = '';
         Actions.updateScene(sceneId, { script: '' });
      }
   }, [content, editor, sceneId]);

   useEffect(() => {
      if (!sceneId || value === lastSavedRef.current) return undefined;

      if (saveTimeoutRef.current) {
         clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
         setIsSaving(true);
         lastSavedRef.current = value;
         Actions.updateScene(sceneId, { script: value });

         if (savingIndicatorTimeoutRef.current) {
            clearTimeout(savingIndicatorTimeoutRef.current);
         }
         savingIndicatorTimeoutRef.current = setTimeout(() => setIsSaving(false), 400);
      }, 800);

      return () => {
         if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
         }
      };
   }, [sceneId, value]);

   useEffect(
      () => () => {
         if (sceneId && valueRef.current !== lastSavedRef.current) {
            lastSavedRef.current = valueRef.current;
            Actions.updateScene(sceneId, { script: valueRef.current });
         }
         if (savingIndicatorTimeoutRef.current) {
            clearTimeout(savingIndicatorTimeoutRef.current);
         }
      },
      [sceneId],
   );

   useEffect(() => {
      if (!showZen) return undefined;

      const closeOnEscape = (event: KeyboardEvent) => {
         if (event.key === 'Escape' && !event.defaultPrevented) {
            setShowZen(false);
         }
      };

      window.addEventListener('keydown', closeOnEscape);
      return () => window.removeEventListener('keydown', closeOnEscape);
   }, [showZen]);

   if (!editor) return null;

   return (
      <div className="space-y-2">
         {!showZen && <EditorSurface editor={editor} isSaving={isSaving} />}

         <div className="flex items-center justify-between">
            <p className="text-[11px] font-mono text-ash">
               Changes save automatically. Type @ for entities or / for commands.
            </p>
            <button
               type="button"
               onClick={() => setShowZen(true)}
               className="text-[11px] font-mono text-silver hover:text-pearl px-2 py-1 bg-charcoal rounded transition-colors"
            >
               Zen mode
            </button>
         </div>

         {showZen && (
            <div className="fixed inset-0 z-50 bg-black flex flex-col text-pearl">
               <div className="flex items-center justify-end px-4">
                  <button
                     type="button"
                     onClick={() => setShowZen(false)}
                     className="px-3 py-2 text-silver hover:text-pearl text-2xl transition-colors opacity-60 hover:opacity-100"
                     aria-label="Close zen mode"
                  >
                     ×
                  </button>
               </div>
               <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 pb-10">
                  <div className="w-full max-w-[920px] mx-auto">
                     <EditorSurface editor={editor} isSaving={isSaving} zen />
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}

export default SceneEditor;
